/**
 * Card component module.
 * @module shared/components/Card
 *
 */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import {fill, layoutAttribute} from './cssUtils';
import {Column} from '../index';

export const _Title = styled.div`
  text-align: left;
  margin: 10px;
  color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.95)'};
  font-family: ${props => props.theme.titleText.name};
  font-size: ${props => props.theme.titleText.size};
  line-height: ${props => props.theme.titleText.lineHeight};
  ${fill()}
`;

export const _Divider = styled.hr`
  border-style: solid;
  color: ${(props) => props.theme.dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
  border-width: 1px;
  ${fill()}
  margin: 5px;
`;

const _Card = styled.div`
  position: relative;
  box-sizing: border-box;
  background-color: ${props => props.theme.tileBackgroundColor};
  background-image: ${(props) => 'linear-gradient(' + (props.theme.dark ? 'rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.1)') + ')'};
  border-radius: 6px;
  padding: 5px;
  margin: ${props => props.margin};
  height: ${props => props.height || 'fit-content'};
  box-shadow: ${props => !props.theme.neumorphic ? '1px 1px 5px 1px rgba(0, 0, 0, 0.25)'
    : '1px 1px 5px 1px rgba(0, 0, 0, 0.25), inset -1px -1px rgba(0, 0, 0, 0.5), inset 1px 1px rgba(255, 255, 255, 0.2)'};
  ${props => !!props.minHeight ? `min-height: ${props.minHeight};` : ''};
  display: flex;
  flex-direction: column;
  ${props => fill(props.width)};
  ${layoutAttribute.css}
  ${props => props.maxWidth && `max-width: ${props.maxWidth}`};
  ${props => props.minWidth && `min-width: ${props.minWidth}`};
`;

/**
 * Card component
 * @name Card
 */
const Card = (props) => {
  const {title, height, width, margin = '5px', minWidth, minHeight, maxWidth} = props;
  const attributes = {
    height,
    margin,
    minWidth,
    maxWidth,
    minHeight,
    width,
  };
  return <_Card {...attributes}>
    <Column>
      {title && <_Title>{title}</_Title>}
      {title && <_Divider/>}
      {props.children}
    </Column>
  </_Card>;
};

export default Card;

Card.propTypes = {
  /**
   * Title of Card
   */
  title: PropTypes.string,
  /**
   * React Element children
   */
  children: PropTypes.node,
  /**
   * The content to display inside Card
   */
  content: PropTypes.shape({
    body: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.element,
    ]).isRequired,
  }),
  /**
   * Height of the content to display inside Card
   */
  height: PropTypes.string,
  /**
   * Width of the content to display inside Card
   */
  width: PropTypes.string,
  /**
   * Minimum Height of the content to display inside Card
   */
  minHeight: PropTypes.string,
  /**
   * Minimum Width of the content to display inside Card
   */
  minWidth: PropTypes.string,
  /**
   * Max Width of the content to display inside Card
   */
  maxWidth: PropTypes.string,
  /**
   * Optional margin defaults to 5px
   */
  margin: PropTypes.string,
};
